import { colorwayIndexRows, resolveIndexThemeId } from '@apps/card/utils/colorway';
import type { ThemeEmbeddings } from '@shared/utils/search';
import { usePrecomputedEmbeddings } from '@shared/utils/search';
import { useMemo } from 'react';
import { colorFacetOptionForColorName } from './components/Filters/facetOptions';
import type { StationeryTemplate } from '@apps/card/types/stationeryTemplate.types';

/**
 * What every color hit says: this template matches this color, this prominently.
 * `rank` is the palette slot — `0` dominant, `1` secondary, `2` accent.
 */
type ColorHitBase = Readonly<{
  templateId: string;
  themeId: string;
  colorOption: string;
  rank: number;
}>;

/**
 * A template with no colorways. Its base index row describes the whole card, so
 * there is no colorway to name and nothing for a swatch to select.
 */
export type TemplateColorHit = ColorHitBase;

/**
 * One (template × colorway) pair whose palette matches a selectable color, e.g.
 * `boldly`'s `tomato` colorway matching `color:red`. `optionIndex` is its position
 * in the template's catalog colorway order; ties break on it.
 */
export type ColorwayColorHit = ColorHitBase & Readonly<{ colorwayId: string; optionIndex: number }>;

/**
 * The gallery's color matches, kept apart by what they can answer rather than
 * merged behind an optional field. The filter and the counts ask "does this
 * template match this color", which `all` answers for both kinds; swatch ordering
 * asks "which colorway should the tile show", which only `colorways` can.
 */
export type ColorHit = TemplateColorHit | ColorwayColorHit;

export type ColorHits = Readonly<{
  all: ReadonlyArray<ColorHit>;
  colorways: ReadonlyArray<ColorwayColorHit>;
}>;

export const EMPTY_COLOR_HITS: ColorHits = { all: [], colorways: [] };
export const EMPTY_COLORWAY_ORDER: ReadonlyMap<string, ReadonlyArray<string>> = new Map();

/**
 * How many of a palette's colors match on area alone, taken in the index's
 * prominence order: the dominant (~75% by area) and the secondary (~20%).
 * Matching the ~5% accent indiscriminately made near-every card match
 * near-every color — on the catalogue of 2026-10-16 it took black from 199 rows
 * to 391 and white from 646 to 941.
 *
 * Note this is a narrower question than search's "what color is this card?",
 * which is why there is no equivalent of the white/beige promotion
 * `colorSimilarity` applies — demoting a neutral here would rank a
 * white-dominant colorway below a merely white-accented one under White.
 */
const MATCHABLE_COLOR_COUNT = 2;

/**
 * The accent slot matches for these, and only these.
 *
 * A metal is an ink or a foil treatment, not a ground: on the catalogue of
 * 2026-10-16 gold almost never reached the dominant or secondary slot, so
 * area-only matching left the Gold filter missing ~40 of the 137 rows the index
 * calls gold. Every other color does reach those slots when it is actually the
 * card's color, so for them the accent adds noise rather than coverage.
 */
const ACCENT_MATCHABLE_COLOR_NAMES: ReadonlySet<string> = new Set(['gold', 'silver', 'roseGold']);

const isMatchableSlot = (colorName: string, slot: number): boolean =>
  slot < MATCHABLE_COLOR_COUNT || (slot === MATCHABLE_COLOR_COUNT && ACCENT_MATCHABLE_COLOR_NAMES.has(colorName));

/**
 * The one thing both kinds of hit share: an index row's palette turned into the
 * filter colors it satisfies, best slot first. A row can hold the same color in
 * several slots, and iterating in order means the rank kept is the better one.
 */
function paletteColorMatches(colorNames: ReadonlyArray<string>): Array<{ colorOption: string; rank: number }> {
  const matches: Array<{ colorOption: string; rank: number }> = [];
  const seen = new Set<string>();
  colorNames.forEach((colorName, rank) => {
    const name = colorName ?? '';
    if (!isMatchableSlot(name, rank)) return;
    const colorOption = colorFacetOptionForColorName(name);
    if (!colorOption || seen.has(colorOption)) return;
    seen.add(colorOption);
    matches.push({ colorOption, rank });
  });
  return matches;
}

/**
 * Every color match in the catalogue, read off the theme-search index — now the
 * only source the Colors filter has, since `stationeryTemplate.tags` is gone.
 *
 * A template is one case or the other, and the split happens here rather than
 * downstream: a card with colorways contributes one hit per colorway, and a card
 * without contributes its base row, which describes the whole card.
 *
 * Rows resolve against a color-aware predicate, so a suite card indexed with an
 * empty palette (`felicity_details`, `emilio_hotels`) falls through to the parent
 * that carries one instead of stopping there with nothing.
 */
export function collectColorHits(templates: ReadonlyArray<StationeryTemplate>, themes: Map<string, ThemeEmbeddings> | undefined): ColorHits {
  if (!themes) return EMPTY_COLOR_HITS;
  const hasColorRow = (key: string) => !!themes.get(key)?.colorNames.length;
  const paletteOf = (key: string | undefined) => (key ? (themes.get(key)?.colorNames ?? []) : []);

  const all: ColorHit[] = [];
  const colorways: ColorwayColorHit[] = [];

  for (const template of templates) {
    const options = template.themeJSON?.card?.front?.colorways?.options;
    const { id: templateId, themeId } = template;

    if (!options?.length) {
      for (const match of paletteColorMatches(paletteOf(resolveIndexThemeId(themeId, hasColorRow)))) {
        all.push({ templateId, themeId, ...match });
      }
      continue;
    }

    for (const row of colorwayIndexRows(themeId, template.themeJSON?.card?.front?.colorways, hasColorRow)) {
      // `options` is non-empty here, so every row names its colorway.
      const colorwayId = row.colorwayId as string;
      for (const match of paletteColorMatches(paletteOf(row.indexKey))) {
        const hit: ColorwayColorHit = { templateId, themeId, colorwayId, optionIndex: row.optionIndex, ...match };
        colorways.push(hit);
        all.push(hit);
      }
    }
  }

  return { all, colorways };
}

/**
 * `themeId` → matching colorway ids, most prominently-colored first, for the
 * selected color options. Shaped like the search-produced order (keyed by
 * `themeId`, since colorways are theme-level and identical across orientations)
 * so tiles consume both through one context.
 */
export function colorwayOrderForSelection(hits: ReadonlyArray<ColorwayColorHit>, selectedColorOptions: ReadonlySet<string>): ReadonlyMap<string, ReadonlyArray<string>> {
  if (!selectedColorOptions.size) return EMPTY_COLORWAY_ORDER;

  const matching = hits.filter(hit => selectedColorOptions.has(hit.colorOption));
  if (!matching.length) return EMPTY_COLORWAY_ORDER;

  // Dominant-color matches first, then catalog order — a deterministic ordering
  // (unlike search, there is no relevance score to sort on).
  const sorted = [...matching].sort((a, b) => a.rank - b.rank || a.optionIndex - b.optionIndex);

  const order = new Map<string, string[]>();
  for (const hit of sorted) {
    const colorwayIds = order.get(hit.themeId) ?? [];
    if (!colorwayIds.includes(hit.colorwayId)) colorwayIds.push(hit.colorwayId);
    order.set(hit.themeId, colorwayIds);
  }
  return order;
}

/**
 * Computed once per gallery render pass and shared by the filter (which
 * templates survive, which colorway each tile shows) and the facet counts, so
 * both report the same set. Empty until the embeddings index loads, at which
 * point the color facet appears at all.
 */
export function useColorHits(templates: ReadonlyArray<StationeryTemplate>): ColorHits {
  const { themes } = usePrecomputedEmbeddings();
  return useMemo(() => collectColorHits(templates, themes), [templates, themes]);
}
